/**
 * The rules every client email template is reviewed against (`email.review`)
 * — internal. Each rule is a check written for the reviewer, a severity, and
 * the words a client reads when its template fails it. Those words are the
 * only part of a rule a client ever sees, so they say what to change and
 * never who or what found it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  ErrorState,
  Input,
  LoadingState,
  ReadOnly,
  Select,
  Switch,
  Textarea,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

import { refusal } from './api-refusal';
import { EMAIL_ENDPOINT, emailKeys, type ReviewPolicy, type ReviewRule } from './email';

const URL = `${EMAIL_ENDPOINT}/review-rules`;

export function EmailReviewRules() {
  const found = useQuery({ queryKey: emailKeys.reviewRules, queryFn: () => api.get<ReviewPolicy>(URL) });
  if (found.isLoading) return <LoadingState label="Loading the review rules" />;
  if (found.error || !found.data)
    return (
      <ErrorState
        message={found.error instanceof ApiError ? found.error.message : 'The rules could not be loaded.'}
        onRetry={() => void found.refetch()}
      />
    );
  // Keyed on what is saved: a colleague's save replaces a stale draft.
  return <Editor key={JSON.stringify(found.data)} saved={found.data} />;
}

function strip(policy: ReviewPolicy): ReviewPolicy {
  const { defaults: _defaults, ...rest } = policy;
  return rest;
}

function Editor({ saved }: { saved: ReviewPolicy }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const [draft, setDraft] = useState<ReviewPolicy>(strip(saved));
  const [resetting, setResetting] = useState(false);
  const changed = JSON.stringify(draft) !== JSON.stringify(strip(saved));
  const save = useMutation({
    mutationFn: () => api.put<ReviewPolicy>(URL, draft),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.reviewRules, data);
      toast.success('Review rules saved', 'Templates saved from now on are reviewed by them.');
    },
  });
  const refused = refusal(save.error);
  const set = (next: Partial<ReviewPolicy>) => {
    save.reset();
    setDraft((was) => ({ ...was, ...next }));
  };
  const setRule = (index: number, next: Partial<ReviewRule>) =>
    set({ rules: draft.rules.map((rule, at) => (at === index ? { ...rule, ...next } : rule)) });

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-4">
        <Alert tone="info">
          Internal. Every version of a client's email template is analysed against these rules before it can be sent, and
          decided by them: a failed blocking rule, or a risk over the line, rejects it. Staff then check the decision. A
          client reads only a failed rule's “client message”.
        </Alert>
        <Card>
          <CardHeader title="How templates are decided" />
          <CardBody className="space-y-4">
            <Switch
              label="Analyse templates automatically"
              hint="Off, every template waits for a member of staff."
              checked={draft.enabled}
              onChange={(event) => set({ enabled: event.target.checked })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Staff check"
                value={draft.staff_check}
                options={[
                  { value: 'after', label: 'After — an approved template can be sent at once' },
                  { value: 'before', label: 'Before — nothing is sent until staff approve' },
                ]}
                onChange={(event) => set({ staff_check: event.target.value as ReviewPolicy['staff_check'] })}
              />
              <Input
                label="Highest risk approved (0–100)"
                inputMode="numeric"
                value={String(draft.max_risk)}
                error={refused?.fields.max_risk}
                onChange={(event) => set({ max_risk: Number(event.target.value) || 0 })}
              />
              <Input
                label="Model"
                hint="provider:model. Empty uses the AI platform's model."
                value={draft.model ?? ''}
                onChange={(event) => set({ model: event.target.value.trim() || null })}
              />
              <Select
                label="Thinking"
                hint="How much the model thinks before judging. Medium thinks as much as the default; low is faster."
                value={draft.reasoning ?? ''}
                options={[
                  { value: '', label: "The model's default" },
                  { value: 'off', label: 'Off (as little as the model allows)' },
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                ]}
                onChange={(event) => set({ reasoning: (event.target.value || null) as ReviewPolicy['reasoning'] })}
              />
              <Input
                label="Tries before staff decide"
                inputMode="numeric"
                value={String(draft.max_attempts)}
                error={refused?.fields.max_attempts}
                onChange={(event) => set({ max_attempts: Number(event.target.value) || 1 })}
              />
            </div>
            <Textarea
              label="Further policies, one a line"
              hint="Anything else the reviewer must hold every template to."
              rows={4}
              value={draft.instructions.join('\n')}
              onChange={(event) =>
                set({
                  instructions: event.target.value
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean),
                })
              }
            />
          </CardBody>
        </Card>
  
        <Card>
          <CardHeader
            title={`Rules (${draft.rules.length})`}
            actions={
              canEdit ? (
                <span className="inline-flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setResetting(true)}>
                    <RotateCcw className="size-3.5" /> Defaults
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      set({
                        rules: [
                          ...draft.rules,
                          {
                            key: `rule_${draft.rules.length + 1}`,
                            title: 'New rule',
                            check: '',
                            severity: 'block',
                            client_message: '',
                            applies_to: 'all',
                            enabled: true,
                          },
                        ],
                      })
                    }
                  >
                    <Plus className="size-3.5" /> Add a rule
                  </Button>
                </span>
              ) : null
            }
          />
          <CardBody>
            <ul className="space-y-4">
              {draft.rules.map((rule, index) => (
                <li key={index} className="space-y-2 rounded-md border border-line p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={rule.severity === 'block' ? 'danger' : 'warning'}>
                      {rule.severity === 'block' ? 'Blocking' : 'Warning'}
                    </Badge>
                    <span className="text-sm font-medium text-heading">{rule.title}</span>
                    <span className="font-mono text-2xs text-faint">{rule.key}</span>
                    <span className="ml-auto inline-flex items-center gap-2">
                      <Switch
                        size="sm"
                        label="On"
                        checked={rule.enabled}
                        onChange={(event) => setRule(index, { enabled: event.target.checked })}
                      />
                      {canEdit ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove ${rule.title}`}
                          onClick={() => set({ rules: draft.rules.filter((_, at) => at !== index) })}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      ) : null}
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-4">
                    <Input
                      label="Key"
                      value={rule.key}
                      onChange={(event) => setRule(index, { key: event.target.value.trim() })}
                    />
                    <Input
                      label="Title"
                      value={rule.title}
                      containerClassName="sm:col-span-1"
                      onChange={(event) => setRule(index, { title: event.target.value })}
                    />
                    <Select
                      label="Severity"
                      value={rule.severity}
                      options={[
                        { value: 'block', label: 'Blocking' },
                        { value: 'warn', label: 'Warning only' },
                      ]}
                      onChange={(event) => setRule(index, { severity: event.target.value as ReviewRule['severity'] })}
                    />
                    <Select
                      label="Applies to"
                      value={rule.applies_to}
                      options={[
                        { value: 'all', label: 'All mail' },
                        { value: 'marketing', label: 'Marketing' },
                        { value: 'transactional', label: 'Transactional' },
                      ]}
                      onChange={(event) => setRule(index, { applies_to: event.target.value as ReviewRule['applies_to'] })}
                    />
                  </div>
                  <Textarea
                    label="What the reviewer checks"
                    rows={3}
                    value={rule.check}
                    onChange={(event) => setRule(index, { check: event.target.value })}
                  />
                  <Textarea
                    label="What the client reads when it fails"
                    hint="Say what to change. Never mention a review, a reviewer or how it was found."
                    rows={2}
                    value={rule.client_message}
                    onChange={(event) => setRule(index, { client_message: event.target.value })}
                  />
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
  
        {refused?.general.length || Object.keys(refused?.fields ?? {}).length ? (
          <Alert tone="danger">
            {[...(refused?.general ?? []), ...Object.values(refused?.fields ?? {})].join(' ')}
          </Alert>
        ) : null}
        {canEdit && changed ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                save.reset();
                setDraft(strip(saved));
              }}
            >
              Discard
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save rules
            </Button>
          </div>
        ) : null}
        <Confirm
          open={resetting}
          onClose={() => setResetting(false)}
          title="Go back to the default rules?"
          confirmLabel="Use the defaults"
          message="The rules on screen are replaced with the ones Connect ships with. Nothing is saved until you save."
          onConfirm={() => {
            set({ rules: saved.defaults ?? draft.rules });
            setResetting(false);
          }}
        />
      </div>
    </ReadOnly>
  );
}
